import { SelectLazyLoading } from '@isdd/idsk-ui-kit/index'
import { SortType } from '@isdd/idsk-ui-kit/types'
import React, { JSX, SetStateAction, useCallback, useEffect, useState } from 'react'
import { MultiValue, OptionProps } from 'react-select'
import { Option } from '@isdd/idsk-ui-kit/common/SelectCommon'
import { FieldValues, UseFormClearErrors, UseFormSetValue } from 'react-hook-form'

import {
    ConfigurationItemUi,
    FilterAttributesUi,
    FilterMetaAttributesUi,
    getReadCiList1QueryKey,
    RelTypeFilterUi,
    useReadCiList1,
    useReadCiList1Hook,
} from '@isdd/metais-common/api/generated/cmdb-swagger'
import { ATTRIBUTE_NAME } from '@isdd/metais-common/api/constants'
import { ENTITY_ACTIVITY, ENTITY_FORMULAR, PO } from '@isdd/metais-common/constants'
import { Link } from 'react-router-dom'

interface ICiLazySelect<T extends FieldValues> {
    ciType: string
    selectedCi?: ConfigurationItemUi | MultiValue<ConfigurationItemUi> | undefined
    label: string
    setSelectedCi?: React.Dispatch<SetStateAction<ConfigurationItemUi | MultiValue<ConfigurationItemUi> | undefined>>
    placeholder?: string
    name?: string
    error?: string
    setValue?: UseFormSetValue<T>
    clearErrors?: UseFormClearErrors<T>
    disabled?: boolean
    defaultValue?: string | string[]
    info?: string
    metaAttributes?: FilterMetaAttributesUi
    relTypeFilters?: RelTypeFilterUi[]
    attributes?: FilterAttributesUi[]
    option?: (props: OptionProps<ConfigurationItemUi>) => JSX.Element
    required?: boolean
    isClearable?: boolean
    isMulti?: boolean
    tabIndex?: number
}

export const CiLazySelect = <T extends FieldValues>({
    ciType,
    selectedCi,
    setSelectedCi,
    label,
    placeholder,
    setValue,
    clearErrors,
    error,
    name,
    disabled,
    defaultValue,
    info,
    metaAttributes,
    relTypeFilters,
    attributes,
    option,
    required,
    isClearable = true,
    isMulti = false,
    tabIndex,
}: ICiLazySelect<T>) => {
    const ciOptionsHook = useReadCiList1Hook()

    const { data } = useReadCiList1(
        {
            filter: { type: [ciType], uuid: defaultValue ? (Array.isArray(defaultValue) ? defaultValue : [defaultValue]) : undefined },
        },
        {
            query: {
                queryKey: getReadCiList1QueryKey({
                    filter: { type: [ciType], uuid: defaultValue ? (Array.isArray(defaultValue) ? defaultValue : [defaultValue]) : undefined },
                }),
                enabled: !!(defaultValue && defaultValue.length > 0),
            },
        },
    )

    const defaultValueKey = Array.isArray(defaultValue) ? defaultValue.join(',') : (defaultValue ?? '')

    const [seed, setSeed] = useState(1)
    useEffect(() => {
        setSeed(Math.random())
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, defaultValueKey, ciType, selectedCi])

    useEffect(() => {
        defaultValue && setSelectedCi && setSelectedCi(data?.configurationItemSet?.[0])
    }, [data, setSelectedCi, defaultValue])

    const loadCiOptions = useCallback(
        async (searchQuery: string, additional: { page: number } | undefined) => {
            const page = !additional?.page ? 1 : (additional?.page || 0) + 1

            const ciResponse = await ciOptionsHook({
                page,
                perpage: 20,
                sortBy: ATTRIBUTE_NAME.Gen_Profil_nazov,
                sortType: SortType.ASC,
                filter: {
                    type: [ciType],
                    searchFields: [ATTRIBUTE_NAME.Gen_Profil_nazov, ATTRIBUTE_NAME.Gen_Profil_kod_metais],
                    relTypeFilters: relTypeFilters ?? [],
                    fullTextSearch: searchQuery,
                    metaAttributes: { ...(metaAttributes ?? undefined) },
                    attributes: attributes ?? [],
                },
            })

            return {
                options: ciResponse.configurationItemSet || [],
                hasMore: page < (ciResponse.pagination?.totalPages ?? 0),
                additional: {
                    page,
                },
            }
        },
        [ciOptionsHook, ciType, metaAttributes, attributes, relTypeFilters],
    )

    const selectLazyLoadingCiOption = (props: OptionProps<ConfigurationItemUi>) => {
        return (
            <Option {...props}>
                <div>
                    <span>{props.data.attributes?.Gen_Profil_nazov as string}</span>
                    <span>{ciType === PO ? `(${props.data.attributes?.EA_Profil_PO_ico})` : ''}</span>
                </div>
            </Option>
        )
    }

    const onChangeProps = {
        ...(setSelectedCi
            ? {
                  onChange: (val: ConfigurationItemUi | MultiValue<ConfigurationItemUi> | null) => {
                      setSelectedCi(val ? val : undefined)
                  },
                  value: selectedCi,
              }
            : {}),
    }

    return (
        <SelectLazyLoading<ConfigurationItemUi>
            key={seed}
            placeholder={placeholder}
            name={name ?? 'account'}
            label={label}
            getOptionValue={(item) => item.uuid?.toString() || ''}
            getOptionLabel={(item) => {
                return ciType === ENTITY_FORMULAR ? (
                    <Link to={`/ci/${ciType}/${item?.uuid}`} state={{ from: location }} target="_blank">
                        {item?.attributes?.Gen_Profil_nazov as string}
                    </Link>
                ) : (
                    (item?.attributes?.Gen_Profil_nazov as string)
                )
            }}
            option={(props) => (option ? option(props) : selectLazyLoadingCiOption(props))}
            loadOptions={(searchTerm, _, additional) => loadCiOptions(searchTerm, additional)}
            {...onChangeProps}
            setValue={setValue}
            clearErrors={clearErrors}
            error={error}
            disabled={disabled}
            isClearable={isClearable}
            defaultValue={defaultValue ? (Array.isArray(defaultValue) ? data?.configurationItemSet : data?.configurationItemSet?.[0]) : undefined}
            info={info}
            required={required}
            isMulti={isMulti}
            tabIndex={tabIndex}
        />
    )
}
